import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable, Injector, isDevMode } from '@angular/core';
import { marker } from '@common/translate-marker';
import { TranslateService } from '@ngx-translate/core';
import { ErrorService } from './services/error.service';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
    constructor(
        private injector: Injector,
        private translateService: TranslateService
    ) {}

    handleError(error: Error | HttpErrorResponse) {
        const errorService = this.injector.get(ErrorService);

        let errorMessages: string[];

        if (error?.name === 'ChunkLoadError' || error?.message?.includes('ChunkLoadError')) {
            console.warn('ChunkLoadError detected. Reloading app...');
            window.location.reload();
        } else if (error?.message?.includes('Invalid token') || error?.message?.includes('Невалиден Жетон')) {
            console.warn('Invalid token');
        } else if (this.isBenignRouterError(error)) {
            // Overlapping router navigations during forced logout / session expiry can reject with
            // NG04008 (NULLISH_COMMAND) or a navigation-cancellation error. These are not actionable
            // by the user, so swallow them instead of showing an "Unexpected error" toast.
            if (isDevMode()) {
                console.warn('Suppressed benign router error:', error);
            }
        } else {
            if (!(error instanceof HttpErrorResponse)) {
                // 403 already displays error to user in server-error-interceptor.ts
                if ((error as any)?.rejection?.status === 403) {
                    return;
                }

                //  Client errors. Server errors are handled in the ServerErrorInterceptor
                if (isDevMode()) {
                    console.error(error);
                }
                const title = this.translateService.instant(marker('Unexpected error:'));
                errorMessages = errorService.getClientMessage(error);
                if (errorMessages[0].includes('NG')) {
                    errorMessages = errorMessages.map((errorMessage) =>
                        errorMessage.replace(/^NG\d+: /, '').replace(/Find more at .+$/, '')
                    );
                }

                errorService.displayErrorInToasty(errorMessages, title);
            }
        }
    }

    private isBenignRouterError(error: Error | HttpErrorResponse): boolean {
        if (error instanceof HttpErrorResponse) {
            return false;
        }
        const message = error?.message ?? '';
        // NG04008 = Angular Router NULLISH_COMMAND: router.navigate([...]) received a null/undefined
        // segment. NavigationCancelingError = a navigation superseded by another. Both are thrown by
        // the redirect churn during forced logout and carry no information the user can act on.
        return (
            message.includes('NG04008') ||
            message.includes('NavigationCancelingError') ||
            (error as { ngNavigationCancelingError?: boolean })?.ngNavigationCancelingError === true
        );
    }
}
